import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { cabinetApi, type OrgSummary } from './api'
import { useAuth } from './auth'
import { pickActiveOrg, orgToAutoSync } from './orgHelpers'

export { pickActiveOrg }

interface OrgContextValue {
  orgs: OrgSummary[]
  activeOrg: OrgSummary | undefined
  activeOrgId: number | null
  isLoading: boolean
  switchOrg: (orgId: number) => Promise<void>
}

const OrgContext = createContext<OrgContextValue | null>(null)

export function OrgProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const { user, setSession } = useAuth()
  const { data, isLoading } = useQuery({
    queryKey: ['cabinet', 'orgs'],
    queryFn: () => cabinetApi.orgs(),
  })

  const orgs = data?.orgs ?? []
  const activeOrgId = data?.active_org_id ?? null
  const activeOrg = pickActiveOrg(orgs, activeOrgId)

  const switchOrg = async (orgId: number) => {
    const res = await cabinetApi.switchOrg(orgId)
    // прогнать новый токен через AuthProvider, чтобы token/user в контексте совпали
    if (user) {
      setSession(res.access_token, { ...user, active_org_id: res.active_org_id })
    }
    // новый токен несёт другую active_org_id → сбросить весь кэш (clear() уже
    // роняет данные и триггерит рефетч смонтированных наблюдателей)
    queryClient.clear()
  }

  // Самолечение: токен без active_org_id (выдан до назначения кабинета / сразу
  // после одобрения заявки), но членство уже есть → org-скоупные эндпоинты
  // (/layers, /species, /data/prepare) отвечают 403, и слои/виды молча пустеют,
  // хотя в шапке визуально «выбрана» orgs[0]. Досинхронизируем токен на первую
  // организацию — без ручного релогина. Однократно, guard от гонки switch-in-flight.
  const syncing = useRef(false)
  useEffect(() => {
    const target = orgToAutoSync(orgs, activeOrgId, isLoading)
    if (target == null || syncing.current) return
    syncing.current = true
    void switchOrg(target).finally(() => {
      syncing.current = false
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, activeOrgId, orgs])

  return (
    <OrgContext.Provider value={{ orgs, activeOrg, activeOrgId, isLoading, switchOrg }}>
      {children}
    </OrgContext.Provider>
  )
}

export function useOrg(): OrgContextValue {
  const ctx = useContext(OrgContext)
  if (!ctx) throw new Error('useOrg must be used within OrgProvider')
  return ctx
}
